import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Button, Spin, message, Tag } from 'antd'
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  UserOutlined,
  TeamOutlined,
  CheckCircleOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { now as tzNow, todayKey } from '../utils/timezone'
import { fetchPendingAssignments, getAssignError } from '../api/booking'
import { usePermission } from '../hooks/usePermission'
import AssignStylistModal from '../components/booking/AssignStylistModal'

/* ════════════════════════════════════════════════════════
   待指派預約（獨立頁）
   ────────────────────────────────────────────────────────
   客人選「不指定人員」的預約，時段已經留下來了，但還沒有人負責。
   預約列表頁上方只放提示與前三筆快捷鍵；一多起來（十幾筆）
   就要有一個能一次看完、逐筆處理的地方，這頁就是。
   依日期分組，處理完一筆該筆就消失，清空 = 沒事了。
   ════════════════════════════════════════════════════════ */

const Page = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 0 40px;
`

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: baseline;
  gap: 10px;
  small {
    font-size: 13px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.45);
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`

const Card = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-left: 3px solid ${({ $today }) => ($today ? '#d5728a' : '#c98a3d')};
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const Time = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  letter-spacing: 0.5px;
  display: flex;
  align-items: baseline;
  gap: 8px;
  .date {
    font-size: 13px;
    font-weight: 600;
    color: #8a6430;
  }
`

const Customer = styled.div`
  font-size: 14px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;
  span.phone {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
  }
`

const Service = styled.div`
  font-size: 13px;
  color: #666;
  line-height: 1.5;
`

const Note = styled.div`
  font-size: 12px;
  color: #999;
  line-height: 1.5;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
  padding-top: 10px;
  border-top: 1px solid #f5f5f5;
`

const Done = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
  .anticon {
    font-size: 40px;
    color: #c9c9c9;
    margin-bottom: 10px;
  }
  p {
    margin: 4px 0 0;
    font-size: 13px;
  }
`

const WEEK = ['日', '一', '二', '三', '四', '五', '六']

export default function PendingAssignmentsPage() {
  const navigate = useNavigate()
  const { can } = usePermission()
  const canAssign = can('assignment:manage')

  const [loading, setLoading] = useState(true)
  const [bookings, setBookings] = useState([])
  const [target, setTarget] = useState(null)

  const load = useCallback(async () => {
    try {
      const data = await fetchPendingAssignments()
      setBookings(data.bookings || [])
    } catch (e) {
      message.error(getAssignError(e, '載入待指派預約失敗'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // 「今天／明天」要以店家時區判斷，不然店家人在 UTC+7 的深夜，
  // 台灣今天的單會被標成「明天」
  const today = todayKey()
  const tomorrow = tzNow().add(1, 'day').format('YYYY-MM-DD')
  const dateLabel = (d) => {
    // d 是 DB 的牆上日期字串，dayjs 解析成本地午夜再格式化回去，
    // 任何時區結果都一樣，這裡維持原樣即可
    const dj = dayjs(d)
    const base = `${dj.format('M/D')}（${WEEK[dj.day()]}）`
    if (d === today) return `今天 ${base}`
    if (d === tomorrow) return `明天 ${base}`
    return base
  }

  return (
    <Page>
      <TopBar>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button
            type='text'
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/booking-detail')}
          >
            預約管理
          </Button>
          <Title>
            待指派預約
            {bookings.length > 0 && <small>共 {bookings.length} 筆</small>}
          </Title>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          重新整理
        </Button>
      </TopBar>

      {!canAssign && bookings.length > 0 && (
        <div
          style={{
            background: '#fffaf2',
            border: '1px solid #f3e3c6',
            borderRadius: 10,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
            color: '#8a6430'
          }}
        >
          只有店主與店長可以指派人員，你目前只能檢視。
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin />
        </div>
      ) : bookings.length === 0 ? (
        <Done>
          <CheckCircleOutlined />
          <div style={{ fontSize: 15 }}>沒有待指派的預約</div>
          <p>客人選「不指定人員」的預約會出現在這裡</p>
        </Done>
      ) : (
        <Grid>
          {bookings.map((b) => {
            const d = String(b.date).slice(0, 10)
            return (
                <Card key={b._id} $today={d === today}>
                  <Row>
                    <Time>
                      <span className='date'>{dateLabel(d)}</span>
                      {b.time}
                    </Time>
                    <Tag color='gold' style={{ margin: 0 }}>
                      待指派
                    </Tag>
                  </Row>
                  <Customer>
                    {b.participantCount > 1 ? <TeamOutlined /> : <UserOutlined />}
                    {b.userName || '客人'}
                    {b.participantCount > 1 && `　${b.participantCount} 人`}
                    {b.phone && <span className='phone'>{b.phone}</span>}
                  </Customer>
                  <Service>
                    {b.productName}
                    {b.duration ? `　${b.duration} 分鐘` : ''}
                  </Service>
                  {b.note && <Note>備註：{b.note}</Note>}
                  <Actions>
                    <Button
                      type='primary'
                      size='small'
                      disabled={!canAssign}
                      onClick={() => setTarget(b)}
                    >
                      指派人員
                    </Button>
                    <Button
                      size='small'
                      onClick={() =>
                        navigate(`/bookings/${b._id}`, { state: { booking: b } })
                      }
                    >
                      查看詳情
                    </Button>
                  </Actions>
                </Card>
            )
          })}
        </Grid>
      )}

      <AssignStylistModal
        booking={target}
        onClose={() => setTarget(null)}
        onAssigned={load}
      />
    </Page>
  )
}
